import type { ReactNode } from 'react';
import type { Step } from 'react-joyride';
import { Typography } from '@mui/material';

function P({ children }: { children: ReactNode }) {
  return <Typography variant="body2" component="div" sx={{ lineHeight: 1.6 }}>{children}</Typography>;
}

export interface AppTourStepOptions {
  isLgUp: boolean;
  showBottomBar: boolean;
  isHome: boolean;
  /** Solo flujo de cobro (p. ej. operador, sin secciones admin en el cajón). */
  payOnlyMode: boolean;
  /** Muestra la tecla «Más» en la barra inferior (más de un destino o admin). */
  showBottomBarMore: boolean;
}

function menuBody(o: AppTourStepOptions) {
  if (o.payOnlyMode) {
    return (
      <P>
        Desde el menú (<strong>☰</strong>) abrís y cerrás el panel: cobro y <strong>cerrar sesión</strong>. (Sin acceso a administración.)
      </P>
    );
  }
  return (
    <P>
      Desde el menú (<strong>☰</strong>) abrís y cerrás el panel lateral con <strong>Inicio</strong>, administración y más opciones.
    </P>
  );
}

export function buildAppTourSteps(o: AppTourStepOptions): Step[] {
  const steps: Step[] = [
    {
      target: 'body',
      placement: 'center',
      disableBeacon: true,
      content: (
        <P>
          Te mostramos un recorrido corto por el portal: menú, contenido y atajos. Podés salir con <strong>Omitir</strong> cuando quieras.
        </P>
      ),
    },
    {
      target: '[data-tour="tour-menu-btn"]',
      disableOverlayClose: true,
      spotlightClicks: true,
      content: menuBody(o),
    },
  ];

  if (o.isLgUp) {
    steps.push({
      target: '[data-tour="tour-drawer-list"]',
      content: o.payOnlyMode
        ? (
            <P>
              En desktop el panel izquierda es <strong>el mismo que ven otros usuarios de la org.</strong> En tu perfil (operador) el menú trae <strong>«Elegir y pagar»</strong> y cerrar sesión, sin cámaras, tarifas, pagos ni logs.
            </P>
          )
        : (
            <P>
              En pantallas grandes el listado queda a la izquierda. Cada ítem te lleva a esa sección (según tu rol y permisos).
            </P>
          ),
    });
  } else {
    const mobileCelularPaso = (() => {
      if (o.payOnlyMode) {
        return (
          <P>
            En el teléfono <strong>se ve igual</strong> que con admin (mismos colores, barra y cajón), pero con{' '}
            {o.showBottomBar ? (<>el acceso a <strong>«Elegir y pagar»</strong> en la barra inferior; </>) : null}
            el menú solo ofrece cobro y <strong>cerrar sesión</strong>, sin Cámaras ni otras secciones.
          </P>
        );
      }
      if (o.showBottomBar && o.showBottomBarMore) {
        return (
          <P>
            En el celular, además del menú, tenés una <strong>barra inferior</strong> para ir a Inicio y a las pantallas frecuentes, y <strong>Más</strong> para abrir el mismo listado completo.
          </P>
        );
      }
      return (
        <P>
          En el celular, además del menú, tenés una <strong>barra inferior</strong> para ir a Inicio y a las pantallas frecuentes.
        </P>
      );
    })();
    steps.push({
      target: 'body',
      placement: 'center',
      disableBeacon: true,
      content: mobileCelularPaso,
    });
  }

  steps.push({
    target: '[data-tour="tour-main-content"]',
    content: o.payOnlyMode
      ? (
        <P>
          Acá hacés el <strong>flujo de documento e importe</strong> hacia el pago (ventanilla). Misma caja y tipografía que el resto del módulo.
        </P>
        )
      : (
        <P>
          El contenido de cada pantalla aparece acá: tarifas, pagos, listados y formularios según la sección elegida.
        </P>
        ),
  });

  if (o.isHome) {
    steps.push({
      target: '[data-tour="tour-home-fees"]',
      content: o.payOnlyMode
        ? (
            <P>
              En <strong>«Elegir y pagar»</strong> elegís DNI o pasaporte, el número, y luego el importe y el pago hacia Mercado Pago (simulado).
            </P>
          )
        : (
            <P>
              En <strong>Inicio</strong> elegís DNI o pasaporte, ingresás el número y recién después ves el importe y el pago hacia Mercado Pago (simulado).
            </P>
          ),
    });
  }

  if (o.showBottomBar) {
    steps.push({
      target: '[data-tour="tour-bottom-nav"]',
      content: o.payOnlyMode
        ? (
            <P>
              Tocando acá <strong>volvés al cobro</strong> (no hay otras secciones). Es la misma barra que con admin, con menos atajos.
            </P>
          )
        : (
            <P>
              Esta barra fija te permite cambiar de sección sin abrir el menú.
            </P>
          ),
    });
  }

  steps.push({
    target: '[data-tour="tour-appbar-actions"]',
    content: (
      <P>
        Arriba a la derecha: <strong>recorrido</strong> (este tour), <strong>modo claro / oscuro</strong> y tu perfil para cerrar sesión.
      </P>
    ),
  });

  steps.push({
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    content: (
      <P>
        Para soporte operativo, contactá al administrador de tu organización. Podés volver a iniciar el recorrido tocando el ícono de ayuda en la barra superior.
      </P>
    ),
  });

  return steps;
}
